export interface Article {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  categoryName: string;
  coverImage: string;
  author: string;
  date: string;
  readTime: string;
  tags: string[];
  featured: boolean;
}

export const articles: Article[] = [
  {
    slug: 'morning-ritual-tea',
    title: '清晨的第一杯茶：仪式感从每天开始',
    excerpt: '当第一缕阳光穿过窗帘的缝隙，烧一壶水，选一只心爱的茶杯，看着茶叶在水中舒展。这五分钟，是我与自己的约定。',
    content: `<p>清晨六点二十分，闹钟还未响起，身体却已经自然醒来。这是多年养成的习惯——在城市的喧嚣尚未苏醒之前，为自己争取一段完全属于自己的时间。</p>
    <p>烧水壶发出低沉的嗡鸣，我站在厨房的窗前，看着天边由深蓝渐变成淡粉，再由淡粉过渡到金色。这一刻的宁静，是一天中最珍贵的礼物。</p>
    <h2>选茶，也是一种修行</h2>
    <p>今天选什么茶？柜子里整齐排列着大大小小的茶罐：春天的龙井、夏天的茉莉花茶、秋天的大红袍、冬天的普洱。每一个罐子都承载着不同的记忆和季节的气息。</p>
    <p>手指轻轻划过罐身，最终停在那罐武夷岩茶上。这是去年秋天朋友从福建带回来的，打开盖子，一股醇厚的岩骨花香扑面而来，仿佛一瞬间就回到了那个秋高气爽的下午。</p>
    <h2>泡茶的过程，是一场冥想</h2>
    <p>温杯、投茶、注水、出汤——每一个动作都不急不缓。热水注入的瞬间，茶叶在水中旋转、舒展，像是一支优雅的舞蹈。茶汤的颜色由浅入深，香气也逐渐在空气中弥漫开来。</p>
    <p>在这个过程中，手机放在另一个房间，没有任何打扰。只有水声、茶香，和自己均匀的呼吸。这大概就是所谓的"正念"吧——把全部的注意力都放在此刻，放在这一杯茶上。</p>
    <h2>第一口茶，世界安静了</h2>
    <p>双手捧着温热的茶杯，凑近鼻尖深深吸一口气，然后轻轻啜一口。茶汤在舌尖流转，先是微微的苦涩，然后回甘慢慢涌上来，像是清晨的露水滑过喉咙。</p>
    <p>就在这一口茶的时间里，所有的焦虑、匆忙、待办清单，都暂时退到了很远的地方。此刻只有我和这杯茶，以及窗外渐渐亮起来的天光。</p>
    <h2>仪式感的意义</h2>
    <p>有人问我，为什么每天要花这么多时间在喝茶上？我想说，这不仅仅是喝茶，而是在用一个小小的仪式，告诉自己：新的一天开始了，我可以选择用什么样的节奏去面对它。</p>
    <p>在这个什么都讲究"快"的时代，我愿意用一杯茶的时间，让灵魂跟上身体的步伐。这或许就是我对抗焦虑的方式——不是逃避，而是在每一天的开始，先给自己注入一点从容和温柔。</p>
    <p>喝完最后一口茶，天色已经大亮。我收拾好茶具，深吸一口气，准备开始新的一天。但这一次，心里多了一份平静。</p>
    <p>毕竟，一个从一杯好茶开始的早晨，不会太差。</p>`,
    category: 'slow-living',
    categoryName: '慢生活',
    coverImage: 'https://readdy.ai/api/search-image?query=Morning%20tea%20ceremony%20on%20a%20wooden%20table%20by%20the%20window%2C%20soft%20golden%20sunlight%2C%20a%20beautiful%20ceramic%20teacup%20with%20steam%20rising%2C%20a%20small%20teapot%2C%20a%20simple%20flower%20arrangement%20on%20the%20side%2C%20warm%20cream%20and%20amber%20tones%2C%20minimalist%20Japanese%20aesthetic%2C%20peaceful%20morning%20mood%2C%20editorial%20photography&width=1200&height=675&seq=article-tea-01&orientation=landscape',
    author: '苏晚',
    date: '2026-08-08',
    readTime: '8 分钟',
    tags: ['晨间仪式', '茶道', '正念', '慢生活'],
    featured: true,
  },
  {
    slug: 'old-town-walk',
    title: '在老街巷里，听时光讲故事',
    excerpt: '每一条老街都是一本打开的书。斑驳的墙面、磨光的石板路、吱呀作响的木门，都在诉说着属于这座城市的前世今生。',
    content: `<p>周末的下午，我刻意避开了所有热门景点的导航，把手机调成静音，只带着一部胶片相机，走进了城南那片还未被改造的老街区。</p>
    <p>一踏进那条窄窄的巷子，时间仿佛瞬间慢了下来。头顶上，纵横交错的电线把天空切割成不规则的碎片。墙角，一丛野生的牵牛花正肆无忌惮地攀爬着。空气中有淡淡的煤炉味，混着谁家厨房飘出的饭菜香。</p>
    <h2>墙上的时间印记</h2>
    <p>最吸引我的是那些墙壁。不是博物馆里精心保存的文物，而是活生生的、正在呼吸的历史。青砖上的苔藓绿得发亮，雨水冲刷出的痕迹像一幅抽象画。一面斑驳的墙上，还能隐约看到几十年前用红漆写的标语，字迹已经模糊，但那股时代的印记却依然清晰。</p>
    <p>我举起相机，透过取景器看这些墙面。它们不是废墟，而是一本本摊开的史书，记载着这座城市曾经的模样。</p>
    <h2>巷子里的日常诗意</h2>
    <p>转过一个拐角，看到一位老奶奶坐在门口择菜。她动作很慢，每摘一片叶子都仔细端详。旁边的收音机里放着咿咿呀呀的戏曲，一只橘猫蜷在她脚边打盹。</p>
    <p>我远远地按下快门，不想打扰这份宁静。但在心里，我多么羡慕这样的生活节奏——不是为了效率而活，而是为了生活本身。</p>
    <h2>消失与留存</h2>
    <p>走了大约两个小时，巷子到头了。尽头是一堵围墙，墙上贴着一张褪色的公告，上面写着这一片区域即将拆迁改造的消息。</p>
    <p>我愣了一下。原来我记录下的这些画面，可能很快就会从这个城市的地图上消失。那些老墙、老树、老邻居，都将变成记忆中的一个坐标。</p>
    <p>这让我更加确信：记录本身就是一种挽留。用影像、用文字——哪怕只能留下其中万分之一的温度，也是值得的。</p>
    <h2>回家路上的感悟</h2>
    <p>傍晚时分，我沿着来时的路往回走。夕阳把整条巷子染成了温暖的橘色，那些斑驳的墙面在这一刻显得格外动人。</p>
    <p>我想，岁月感之所以迷人，不是因为它让人怀旧，而是因为它提醒我们：一切都正在发生，一切都正在流逝。正因如此，每一个当下才格外珍贵。</p>
    <p>回到家，我把胶卷送去冲洗。不知道那些底片上会呈现出怎样的画面，但我知道，那个下午在老街巷里听到的时光故事，已经永远留在我的心里了。</p>`,
    category: 'sense-of-years',
    categoryName: '岁月感',
    coverImage: 'https://readdy.ai/api/search-image?query=Quiet%20old%20alley%20in%20a%20Chinese%20traditional%20town%2C%20weathered%20brick%20walls%20with%20moss%2C%20narrow%20cobblestone%20path%2C%20sunlight%20filtering%20through%20buildings%2C%20an%20elderly%20person%20sitting%20on%20a%20wooden%20stool%2C%20a%20sleeping%20orange%20cat%2C%20warm%20golden%20hour%20light%2C%20nostalgic%20and%20poetic%20mood%2C%20fine%20art%20documentary%20photography&width=1200&height=675&seq=article-oldtown-02&orientation=landscape',
    author: '林深',
    date: '2026-08-05',
    readTime: '10 分钟',
    tags: ['老街', '城市记忆', '胶片摄影', '岁月感'],
    featured: true,
  },
  {
    slug: 'handmade-ceramics-journey',
    title: '一双手，一团泥，一个下午的手作时光',
    excerpt: '在景德镇的三天里，我把自己完全交给泥土。当指尖触碰到湿润的陶土，世界的喧嚣突然消失了——原来专注，是最好的治愈。',
    content: `<p>决定去景德镇学陶，是某个失眠夜晚的冲动决定。那段时间工作压力很大，每天都感觉被什么东西追赶着，喘不过气来。朋友说，你需要做一些"不用动脑子"的事。于是我想到了陶艺。</p>
    <p>景德镇的空气里都飘着一种特殊的气味——泥土、釉料、松木燃烧后混合在一起的味道。说不上好闻，但让人莫名安心。</p>
    <h2>第一天：与泥土的初次对话</h2>
    <p>工作室的老师是个四十多岁的中年男人，话不多，但手上的功夫让人惊叹。他给我一块陶土，说："先摸摸它，感受它的温度和质地。"</p>
    <p>我双手捧着那块泥土，它微微发凉，带着一种原始而质朴的触感。老师说，每一块泥土都有自己的"性格"，有的软糯，有的硬朗，你要学会倾听它。</p>
    <p>听起来很玄，但当我把泥土放在拉坯机上，手沾了水开始塑形的时候，我真的感觉到了某种"交流"。泥土在旋转中回应着我的双手——用力过猛，它就歪了；太轻了，它纹丝不动。只有找到一个刚刚好的力道，它才会慢慢向上生长，变成一个碗、一个杯子的雏形。</p>
    <h2>失败与接纳</h2>
    <p>当然，失败是常态。第一个作品在快要成型的时候，因为急于求成，手指一个不稳，整个塌了下去。看着那团变形的泥土，我愣了一下，然后忍不住笑了。</p>
    <p>如果在工作中出现这样的"失败"，我大概会懊恼很久。但在这一刻，我居然觉得没什么。大不了重新来过，反正泥土还在这里，时间也还在这里。</p>
    <p>老师说了一句让我印象深刻的话："做陶就是修行。你急，泥就急给你看。你慢下来，它也就慢下来了。"</p>
    <h2>治愈，藏在专注里</h2>
    <p>三天的时间过得飞快。每天从早上九点做到下午五点，中午就在工作室旁边的小摊上吃一碗拌粉。手机全程静音，世界只剩下双手和泥土。</p>
    <p>当最后一天，老师帮我把做好的几个杯子放进窑里的时候，我突然觉得这三天是我近一年来最平静的时光。不是因为没有烦恼，而是因为在专注的过程中，那些烦恼暂时失去了重量。</p>
    <p>我想，这就是手工的意义吧。它让你回到最原始、最直接的状态：用双手创造，用感官体验，用心感受。在这个过程中，你不需要考虑KPI，不需要回复信息，不需要扮演任何角色。你只需要做一件事——让泥土变成你想要的样子。</p>
    <p>或者更准确地说，让泥土变成它想成为的样子。</p>`,
    category: 'healing-life',
    categoryName: '治愈系生活',
    coverImage: 'https://readdy.ai/api/search-image?query=Hands%20shaping%20clay%20on%20a%20pottery%20wheel%2C%20close-up%20shot%2C%20wet%20clay%20spinning%2C%20natural%20light%20from%20a%20workshop%20window%2C%20scattered%20ceramic%20tools%20on%20a%20wooden%20table%2C%20earthy%20terracotta%20and%20cream%20tones%2C%20mindful%20and%20meditative%20atmosphere%2C%20artisan%20craftsmanship%2C%20editorial%20photography&width=1200&height=675&seq=article-ceramics-03&orientation=landscape',
    author: '苏晚',
    date: '2026-08-01',
    readTime: '12 分钟',
    tags: ['陶艺', '手作', '治愈', '专注', '景德镇'],
    featured: true,
  },
  {
    slug: 'film-camera-diary',
    title: '用胶片相机，给生活按下慢放键',
    excerpt: '数码时代，我们拍得太多、太快。直到重新拿起那台老旧的胶片相机，我才发现：慢下来等待一张照片的诞生，本身就是一种幸福。',
    content: `<p>那台美能达X-700是我在二手市场淘来的。机身有些许划痕，皮套上带着岁月的包浆，但快门声清脆有力，像一位精神矍铄的老人。</p>
    <p>装上第一卷胶卷的那一刻，我突然意识到：这卷胶卷只有36张。也就是说，我只有36次按下快门的机会。这和手机摄影完全不同——手机里可以拍几百张然后挑一张，但胶片让你每一次快门都变得慎重。</p>
    <h2>慢下来的观看</h2>
    <p>因为每一张都很珍贵，我开始学会了真正的"观看"。拍一朵花之前，我会先绕着它走一圈，看看光从哪个方向来，背景是什么颜色，花的状态是盛开还是含苞。这些在数码时代被省略的步骤，在胶片时代却是必须的功课。</p>
    <p>有趣的是，当我放慢节奏去观看时，我发现了很多以前从未注意到的细节。路边的野花上停着一只七星瓢虫；老房子的门环上刻着精细的花纹；黄昏的光线穿过树叶，在地面上投下斑驳的光影。</p>
    <p>这些一直都在那里，只是以前的我走得太快，从来没有认真看过。</p>
    <h2>等待的仪式感</h2>
    <p>拍完一卷胶卷后，不能马上看到结果。你需要把它送去冲洗，然后等上三五天甚至更久。这种"延迟满足"在当下几乎是奢侈的——我们已经习惯了即拍即看即分享。</p>
    <p>但正是这种等待，让胶片的魅力加倍。去取照片的路上，心里总是带着一种小小的期待和紧张，像是去见一个许久未见的老朋友。你不知道那些照片会是惊喜还是遗憾，但无论如何，你都会珍惜它们——因为它们是你用心观察、认真记录的结果。</p>
    <h2>瑕疵之美</h2>
    <p>洗出来的照片并不完美。有些曝光不足，有些焦点偏了，有些还带着奇怪的光斑。但我反而更喜欢这些"不完美"的照片。它们有温度、有个性，不像数码照片那样精确到像素却少了灵魂。</p>
    <p>朋友说我疯了，在这个时代还用胶片。但我想说，正是因为这个时代太快了，才更需要一些能让我们慢下来的东西。胶片相机就是其中之一。</p>
    <p>它不会让你变快，它只会让你学会等待，学会珍惜，学会在按下快门之前，先用心去看。</p>`,
    category: 'time-records',
    categoryName: '时光记录',
    coverImage: 'https://readdy.ai/api/search-image?query=Vintage%20film%20camera%20on%20a%20wooden%20table%2C%20scattered%20developed%20film%20negatives%20and%20printed%20photos%2C%20warm%20afternoon%20sunlight%20casting%20soft%20shadows%2C%20a%20cup%20of%20coffee%20nearby%2C%20cream%20and%20sepia%20tones%2C%20nostalgic%20analog%20aesthetic%2C%20artistic%20still%20life%20photography&width=1200&height=675&seq=article-film-04&orientation=landscape',
    author: '林深',
    date: '2026-07-28',
    readTime: '9 分钟',
    tags: ['胶片摄影', '慢生活', '时光记录', '怀旧'],
    featured: false,
  },
  {
    slug: 'weekend-flowers',
    title: '周末清晨去花市，是成年人的游乐场',
    excerpt: '周六早晨七点的花市，是我在这座城市里找到的最治愈的角落。不需要买很多，一束洋甘菊或者几枝尤加利，就够点亮一整周的心情。',
    content: `<p>每周六早上七点，当大多数人还在梦乡中，我已经骑着自行车穿过半座城，来到城东的花卉市场。这是我雷打不动的周末仪式，比任何闹钟都准时。</p>
    <p>花市在一条不起眼的小街上，但一拐进去，整个世界就变了颜色。满眼是层层叠叠的绿、星星点点的红、成片成片的粉。空气里混合着泥土、水汽和各种花香，深吸一口，肺腑都舒展开了。</p>
    <h2>花市里的人间烟火</h2>
    <p>花市最迷人的不只是花，还有人。卖花的大叔用带着口音的普通话大声吆喝；老奶奶推着小车，里面装满了自己种的茉莉花；年轻的女孩蹲在地上，认真地挑选最饱满的多肉植物。</p>
    <p>这里没有写字楼里的客套和距离感，每个人都因为花而变得柔软。卖花的大姐会热情地教你如何养护，旁边的陌生人会主动帮你拿一下买好的花束。在花市里，陌生人之间的善意就像花香一样自然。</p>
    <h2>选花如选心情</h2>
    <p>我每次来都不会提前规划买什么花，而是凭直觉。心情明朗的时候，会挑一束明黄色的向日葵；需要安静的时候，就选白色的洋甘菊或者淡紫色的薰衣草；偶尔也会被某种从未见过的花吸引，带回家慢慢认识它。</p>
    <p>今天挑了一束浅粉色的洋牡丹，老板娘用旧报纸包好，递给我的时候笑着说："这花好看，就是娇气，回家记得斜剪根部，水位别太高。"</p>
    <p>这些朴素的生活智慧，比任何精致的花艺教程都更让人感到温暖。</p>
    <h2>插花，是与自己的对话</h2>
    <p>回到家，把花一支一支地修剪好，插进花瓶里。这个过程不需要任何技巧，只需要耐心和一点点审美——把高的放在中间，矮的放在两边，让每一朵花都有属于自己的空间。</p>
    <p>插好的花摆在餐桌中央，整个房间都变得不一样了。阳光透过花瓣，在地板上投下淡淡的光影。我坐在旁边喝杯茶，看着那些花，觉得这就是最好的一天。</p>
    <p>有人说，买花是浪费钱。但我觉得，花不是装饰品，而是一种生活态度——你愿意为美花时间、花心思、花钱，这就说明你还在认真地对待生活。</p>
    <p>下周的这个时候，我还会准时出现在花市。不知道又会遇见什么花，遇见什么人。</p>`,
    category: 'life-aesthetics',
    categoryName: '生活美学',
    coverImage: 'https://readdy.ai/api/search-image?query=Bustling%20flower%20market%20in%20early%20morning%2C%20colorful%20fresh%20flowers%20in%20buckets%2C%20a%20person%20holding%20a%20bouquet%20wrapped%20in%20newspaper%2C%20soft%20morning%20light%2C%20warm%20and%20lively%20atmosphere%2C%20cream%20and%20pastel%20tones%2C%20lifestyle%20documentary%20photography&width=1200&height=675&seq=article-flowers-05&orientation=landscape',
    author: '苏晚',
    date: '2026-07-22',
    readTime: '7 分钟',
    tags: ['花市', '周末仪式', '生活美学', '插花'],
    featured: false,
  },
  {
    slug: 'sunset-by-the-lake',
    title: '带一本书去湖边，什么都不做地坐一下午',
    excerpt: '最好的周末计划，可能就是没有计划。带上一条毯子、一本书，在湖边找一个树荫坐下。书也许一页都没翻，但心里的褶皱都被熨平了。',
    content: `<p>朋友问我周末有什么安排，我说："去湖边坐坐。"她问做什么，我说："什么都不做。"她愣了一下，然后说："真好。"</p>
    <p>是的，真好。在这个什么都要有"产出"的时代，能理直气壮地说"什么都不做"，本身就是一种奢侈。</p>
    <h2>湖边的时光密度</h2>
    <p>城北的这片湖不大，但胜在安静。工作日的下午几乎没有人，只有几只野鸭子在水面上划来划去，偶尔发出几声嘎嘎的叫声。我找了一棵大柳树下的草地，铺开毯子，把帆布袋里的东西一样一样拿出来：一本书、一瓶水、一个苹果。</p>
    <p>然后我就躺下了。是真的躺下，仰面朝天，透过柳枝的缝隙看天空。云在慢慢地移动，像是有人在天上缓缓地拖着一块白纱。世界安静得只剩下风声和水声，还有远处偶尔传来的鸟鸣。</p>
    <p>在湖边，时间的密度似乎不一样。办公室里的一小时过得飞快，但湖边的一小时却能装下很多东西：云的形状变了三次，一对鸭子夫妇游了两个来回，阳光从柳树的这一边移到了那一边。</p>
    <h2>其实书没怎么看</h2>
    <p>说实话，我带来的那本书只翻了三页。不是因为不好看，而是因为注意力总是被别的东西吸引走了。一片落叶缓缓地飘到我的毯子上；一只蜻蜓停在我膝盖上几秒钟；远处的水面上泛起一圈圈涟漪。</p>
    <p>这些都不是什么惊天动地的大事，但就是这些微小的瞬间，让坐在湖边的那个下午变得无比丰盛。</p>
    <p>我想起木心的一句话："从前的日色变得慢，车、马、邮件都慢。"其实日色没有变，变的是我们的节奏。当我们愿意停下来的时候，日色自然就慢了。</p>
    <h2>什么都不做的哲学</h2>
    <p>太阳快落山的时候，我开始收拾东西准备回去。毯子上沾了几根草，书还是停留在第三页，苹果吃完了，水瓶也空了。从"效率"的角度来看，这个下午毫无产出。但从"生活"的角度来看，这是我近一个月最充实的一个下午。</p>
    <p>"什么都不做"不是懒惰，而是一种能力——在不对任何事情产生焦虑的情况下，纯粹地享受存在的本身。这种能力在快节奏的生活中越来越稀缺，所以才格外珍贵。</p>
    <p>下个周末，如果天气好，我还会去湖边。也许还是什么都不做。</p>`,
    category: 'slow-time',
    categoryName: '慢时光',
    coverImage: 'https://readdy.ai/api/search-image?query=Peaceful%20lakeside%20scene%20under%20a%20large%20willow%20tree%2C%20a%20picnic%20blanket%20on%20green%20grass%2C%20an%20open%20book%20and%20a%20water%20bottle%2C%20sunlight%20filtering%20through%20leaves%2C%20calm%20water%20with%20gentle%20ripples%2C%20cream%20and%20soft%20green%20tones%2C%20serene%20and%20idyllic%20atmosphere%2C%20fine%20art%20landscape%20photography&width=1200&height=675&seq=article-lake-06&orientation=landscape',
    author: '林深',
    date: '2026-07-18',
    readTime: '6 分钟',
    tags: ['湖边', '发呆', '慢时光', '自然'],
    featured: false,
  },
  {
    slug: 'grandma-recipe',
    title: '外婆的厨房：一道菜里的三代人记忆',
    excerpt: '外婆的红烧肉没有什么独门秘方，只有最普通的酱油、冰糖和黄酒。但就是这简单的味道，串联起了我对外婆、对童年、对故乡的全部记忆。',
    content: `<p>每次回家，第一件事就是去外婆的厨房。八十多岁的她，背已经有些驼了，但系上围裙的那一刻，整个人就精神起来，像是回到了五十年前。</p>
    <p>她的厨房不大，甚至可以说是拥挤。墙上挂着用了二三十年的铁锅，灶台上摆着各种瓶瓶罐罐，窗户上贴着已经泛黄的剪纸。但就是这个小空间，盛放了我整个童年的味觉记忆。</p>
    <h2>红烧肉，慢慢来</h2>
    <p>外婆的红烧肉，从准备到上桌至少要两个小时。她不用高压锅，说那样肉没有灵魂。五花肉一定要手工切成两厘米见方的小块，每一块都要带皮带肥带瘦。切肉的时候，外婆的动作很慢，但每一刀都精准无比。</p>
    <p>肉下锅之前要先焯水，然后炒糖色。外婆炒糖色不用一滴水，只用冰糖和一点点油，小火慢慢熬到琥珀色。她说这一步最考验耐心，火大了糖就苦了，火小了上不了色。就像做人——急不得，慢不得，要刚刚好。</p>
    <p>然后加入五花肉翻炒上色，倒入黄酒、酱油，再放几片生姜和一段桂皮。最后加水没过肉块，盖上锅盖，转小火，让时间来完成剩下的工作。</p>
    <h2>等待的味道</h2>
    <p>等肉炖的时候，外婆会搬个小板凳坐在厨房门口，有时择菜，有时就只是看着窗外的天。厨房里飘出越来越浓的肉香，混着酱油和黄酒的醇厚，整个屋子都变得暖洋洋的。</p>
    <p>我小时候最喜欢这个时候。因为这意味着再过不久就能吃到外婆的红烧肉了，那种期待感本身就是一种幸福。现在长大了，这种期待的幸福感一点都没有减少。</p>
    <h2>味道里的传承</h2>
    <p>外婆说，这道红烧肉的做法是从她的外婆那里学来的。那时候条件不好，只有逢年过节才能吃上一次肉。所以每一次做红烧肉，都是一件郑重其事的仪式。</p>
    <p>我忽然意识到，此刻厨房里的味道，已经穿越了四代人的时光。外婆的外婆、外婆、妈妈、我——我们被同一道菜的味道连接在一起。</p>
    <p>这是任何高级餐厅都做不出来的味道。因为它里面不仅有酱油和冰糖，还有一百年的光阴、四代人的手温，以及一个家的全部记忆。</p>
    <p>晚饭的时候，我夹起一块红烧肉放进嘴里。入口即化，肥而不腻，甜咸适中。那一刻，我突然很想哭——不是因为悲伤，而是因为幸福。</p>
    <p>我想，这就是"家"的味道吧。不是有多惊艳，而是让你无论在世界的哪个角落，只要一闻到这个味道，就知道自己回家了。</p>`,
    category: 'sense-of-years',
    categoryName: '岁月感',
    coverImage: 'https://readdy.ai/api/search-image?query=An%20elderly%20Chinese%20grandmother%20cooking%20in%20a%20small%20warm%20kitchen%2C%20steam%20rising%20from%20a%20clay%20pot%2C%20sunlight%20through%20the%20window%2C%20wooden%20cutting%20board%20and%20traditional%20ingredients%2C%20rustic%20and%20homey%20atmosphere%2C%20warm%20amber%20and%20cream%20tones%2C%20nostalgic%20documentary%20photography&width=1200&height=675&seq=article-grandma-07&orientation=landscape',
    author: '苏晚',
    date: '2026-07-15',
    readTime: '11 分钟',
    tags: ['外婆', '家庭味道', '传承', '慢食', '岁月感'],
    featured: true,
  },
  {
    slug: 'winter-reading-list',
    title: '冬日阅读清单：五本让你慢下来的书',
    excerpt: '冬天是最适合读书的季节。窗外寒风呼啸，屋内一盏暖灯、一杯热茶、一本好书。推荐五本适合慢读的书，让文字陪你过冬。',
    content: `<p>冬天一到，我就开始囤书。不是因为冬天比其他季节更有时间读书，而是冬天的气氛太适合读书了——窗外寒风呼啸，屋内温暖如春，泡一杯红茶，裹上毯子，翻开一本书。这是我能想到的最奢侈的冬日享受。</p>
    <p>以下是我今年冬天的五本推荐，每一本都不适合速读，只适合慢慢品味。</p>
    <h2>1. 《山之四季》——高村光太郎</h2>
    <p>这是日本诗人高村光太郎隐居山林期间的随笔集。他一个人住在岩手县的山中小屋里，记录下四季的变化——春天的野花、夏天的雷雨、秋天的落叶、冬天的雪。文字朴素至极，但每一句都像是一首短歌。</p>
    <p>读这本书的时候，你会不自觉地放慢呼吸。它不会教你什么大道理，只是让你看到：原来一个人的生活可以这么简单，这么安静，这么丰盛。</p>
    <h2>2. 《遥远的向日葵地》——李娟</h2>
    <p>李娟的文字有一种独特的魔力——明明写的是新疆阿勒泰那片荒凉的土地，却让人读出了满纸的温柔。她写母亲在戈壁滩上种向日葵，写牧民转场时扬起的尘土，写夜晚星空下的孤独。</p>
    <p>但她从不悲情。她用一种近乎天真的目光去看待生活中的一切，然后轻描淡写地写下来。读完你会有一种冲动：想去看看那片向日葵地，想认识那个倔强又柔软的母亲。</p>
    <h2>3. 《京都如晤》——苏枕书</h2>
    <p>一本关于京都的书信集。作者在京都生活多年，用书信的形式记录了这座古都的日常——寺庙的钟声、鸭川的水流、老铺子的和果子、街角的花店。文字安静而隽永，像是京都本身的气质。</p>
    <p>最适合在一个安静的午后，配着抹茶和羊羹一起读。读完之后，你大概也会想订一张去京都的机票。</p>
    <h2>4. 《日日是好日》——森下典子</h2>
    <p>这是作者跟随茶道老师学习二十五年的心得。表面上是讲茶道，实际上是讲人生。每一篇都短小精悍，但意味深长。其中有一句话我特别记得："不要用自己的好恶去评判事物，任何天气都是好天气，任何日子都是好日子。"</p>
    <p>这本书就像一碗温热的抹茶，初入口微苦，回甘悠长。</p>
    <h2>5. 《理想的下午》——舒国治</h2>
    <p>舒国治被称为"台北的晃荡者"。这本书就是他的"晃荡笔记"——写他在世界各地无所事事地闲逛，写他在咖啡店里看人，写他在凌晨的城市里漫无目的地散步。读他的文字，你会觉得"无所事事"其实是一门很高深的学问。</p>
    <p>五本书，五种不同的慢。希望其中至少有一本能陪你度过这个冬天，让你在寒冷的日子里，也能感受到文字的体温。</p>`,
    category: 'slow-living',
    categoryName: '慢生活',
    coverImage: 'https://readdy.ai/api/search-image?query=Cozy%20winter%20reading%20nook%20with%20a%20stack%20of%20books%20and%20a%20steaming%20cup%20of%20tea%2C%20a%20soft%20wool%20blanket%20draped%20over%20a%20chair%2C%20warm%20glow%20from%20a%20reading%20lamp%2C%20snow%20falling%20outside%20the%20window%2C%20cream%20and%20warm%20amber%20tones%2C%20hygge%20atmosphere%2C%20cozy%20interior%20photography&width=1200&height=675&seq=article-books-08&orientation=landscape',
    author: '林深',
    date: '2026-07-10',
    readTime: '10 分钟',
    tags: ['读书', '冬日', '书单', '慢生活'],
    featured: false,
  },
];

export const articleMap: Record<string, Article> = {};
articles.forEach((article) => {
  articleMap[article.slug] = article;
});

export function getArticlesByCategory(slug: string): Article[] {
  return articles.filter((a) => a.category === slug);
}

export function getFeaturedArticles(): Article[] {
  return articles.filter((a) => a.featured);
}

export function getRelatedArticles(currentSlug: string, category: string, limit: number = 3): Article[] {
  return articles
    .filter((a) => a.category === category && a.slug !== currentSlug)
    .slice(0, limit);
}